import { FC, useEffect, useLayoutEffect, useMemo, useRef, useState, UIEvent } from "react";
import { Tooltip } from "antd";
import classNames from "classnames";
import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import { LoadingStatus, GridRowHeightEnum } from "../../../@types/enumsGlobal";
import { DownIcon, RightIcon } from "../../../libs/corporate/icons/outlined/directions/ChevronCollection";
import { FolderIcon, FolderMinusIcon, FolderPlusIcon } from "../../../libs/corporate/icons/outlined/other/FileAndFolderCollection";
import * as ActionIcons from "../../../libs/corporate/icons/outlined/editor/ActionCollection";
import { } from "../../../Services/Extensions/Boolean";
import { } from "../../../Services/Extensions/DateTime";
import { copyToClipboard } from "../../../system/functions/copyToClipboard";
import { FormattedValueView, getFormattedCellValue } from "../components/FormattedValueView";
import { TableHeader } from "../components/TableHeader";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { useUserContext } from "../../../system/providers/userContextProvider";
import { Spinner } from "../../spiner/Spinner";
import { Spin } from 'antd';
import { DocumentOperationSpinner } from "../../DocumentOperationSpinner/DocumentOperationSpinner";
import { ContextMenu } from "../../contextMenu/contextMenu";
import { IColumnToSave, IGridCellWithWidth } from "../default/defaultGrid";
import defaultStyles from "../default/defaultGrid.module.scss";
import businessStyles from "../../../Business/styles/index.module.scss";
import GridUISettings from "../default/gridUiSettings";
import styles from "./treeGrid.module.scss";
import { reverseConversionWithType } from "../../../system/functions/conversion";
import { LoadingOutlined } from "@ant-design/icons";
import { VirtualGrid } from "../components/virtualGrid/VirtualGrid";
import { useTranslation } from "react-i18next";
import { useGridFontStyle } from "../useGridFontStyle";

function compareByOrder(a: { order?: number }, b: { order?: number }) {
    if (a.order === undefined || b.order === undefined) return 0;
    if (a.order < b.order) return -1;
    if (a.order > b.order) return 1;
    return 0;
}

interface ITreeGridProps {
    gridId: string;
    plugin: IPluginSettings;
    data: any[];
    totalCount: number;
    idParent: string; // Наименование поля с уникальным ID элемента (по нему ищем детей)
    idChildren: string; // Наименование поля со ссылкой на родителя (равно idParent родительского элемента)
    selectedItem?: IGridRow;
    loadingStatus?: LoadingStatus;
    loadingNodes?: Set<string>;
    setLoadingStatus?: (value: LoadingStatus) => void;
    showQuantity?: boolean;
    hideSettingstButton?: boolean;
    getStyleRow?: (row: IGridRow) => string | null;
    onSelect?: (row: IGridRow | undefined) => void;
    onEnter?: (row: IGridRow | undefined) => void;
    onDoubleClick?: (row: IGridRow) => void;
    useUiService?: boolean;
    changeData?: (data: any[]) => any[]; // Функция для подготовки данных перед построением иерархии
    separator?: boolean;
    singleDirectory?: boolean;
    expandedIds?: Set<string>;
    onToggleExpand?: (id: string) => void;
    nodeIdField?: string;
    autoExpandOnLoad?: boolean; // Автоматически раскрывать все узлы при загрузке данных (только при наличии onToggleExpand)
    flat?: boolean; //Показывать данные без иерархии
}

interface ITreeRowComponentData {
    visibleNodes: ITreeVisibleNode[];
    pluginSettings: IPluginSettings;
    columns: IGridColumn[];
    expandedIds?: Set<string>;
    collapsedIds: Set<string>;
    loadingNodes?: Set<string>;
    selectedItem?: IGridRow;
    onToggleExpand: (id: string) => void;
    onSelect?: (row: IGridRow | undefined) => void;
    onDoubleClick?: (row: IGridRow) => void;
    getStyleRow?: (row: IGridRow) => string | null;
    treeColWidth: number;
    nodeIdField?: string;
    idParent: string;
    idChildren: string;
    props: ITreeGridProps;
}

interface ITreeVisibleNode {
    rawData: any;
    row: IGridRow;
    depth: number;
    hasChildren: boolean;
}

const FIXED_ROW_HEIGHT = 44;
const SCROLLBAR_HEIGHT = 15;

export const TreeGrid: FC<ITreeGridProps> = (props) => {
    const appCtx = useAppContext();
    const userCtx = useUserContext();
    const tableRef = useRef<HTMLDivElement>(null);

    const [useUiService] = useState<boolean>(props.useUiService ?? true);
    const defaultPlugin: IPluginSettings = JSON.parse(JSON.stringify(props.plugin)) as IPluginSettings;
    const [pluginSettings, setPluginSettings] = useState<IPluginSettings>(defaultPlugin);
    const [gridColumnsToSave, setGridColumnsToSave] = useState<IColumnToSave>({ columns: [], save: false });
    const assignIdRows = (items: any[]) =>
        items.map((x) => ({ ...x, idRow: uuidv4() }));

    const [data, setData] = useState<any[]>(() => assignIdRows(props.data));
    const [selectedItem, setSelectedItem] = useState<IGridRow | undefined>(undefined);
    const [loadingRow, setLoadingRow] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [internalCollapsedIds, setInternalCollapsedIds] = useState<Set<string>>(new Set());
    const automaticallyExpandedIds = useRef<Set<string>>(new Set());
    const [clientHeight, setClientHeight] = useState<number | null>(getClientHeight(data.length));
    const [scrollLeft, setScrollLeft] = useState(0);
    const savedScrollLeft = useRef<number | null>(null);
    const scrollLockCleanup = useRef<(() => void) | null>(null);
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState<number>(0);

    const [gridHeaderContextMenuVisible, setGridHeaderContextMenuVisible] = useState(false);
    const [contextMenuColumn, setContextMenuColumn] = useState<IGridColumn | undefined>();
    const [showGridHeaderSettings, setShowGridHeaderSettings] = useState(false);
    const [headerCtxPosX, setHeaderCtxPosX] = useState(0);
    const [headerCtxPosY, setHeaderCtxPosY] = useState(0);
    const yDividerPos = useRef<number>(0);

    const rowHeight = pluginSettings.rowHeight ?? GridRowHeightEnum.normal;

    const gridFontStyle = useGridFontStyle();

    const { t } = useTranslation();
    const baseT = (value: string) => t('grids.' + value);

    function getClientHeight(dataLength: number, rowHeight: number = pluginSettings?.rowHeight ?? GridRowHeightEnum.normal) {
        const rowsToShow = 15;
        const visibleRows = Math.min(dataLength, rowsToShow);
        if (dataLength === 0) {
            const height = 65;
            if (tableRef.current) {
                tableRef.current.style.height = height + "px";
            }
            return height;
        }
        if (dataLength === 1) {
            const height = rowHeight * 2 + SCROLLBAR_HEIGHT;
            if (tableRef.current) {
                tableRef.current.style.height = height + "px";
            }
            return height;
        }

        const height = visibleRows * rowHeight + rowHeight + SCROLLBAR_HEIGHT;
        if (tableRef.current) {
            tableRef.current.style.height = height + "px";
        }
        return height;
    }

    const onMouseHoldDown = (e: React.MouseEvent) => {
        yDividerPos.current = e.clientY;
        document.addEventListener("mouseup", onMouseHoldUp);
        document.addEventListener("mousemove", onMouseHoldMove);
    };

    const onMouseHoldUp = () => {
        yDividerPos.current = 0;
        document.removeEventListener("mouseup", onMouseHoldUp);
        document.removeEventListener("mousemove", onMouseHoldMove);
    };

    const onMouseHoldMove = (e: MouseEvent) => {
        if (!yDividerPos.current) {
            return;
        }
        setClientHeight((prev) => (prev !== null ? prev + e.clientY - yDividerPos.current : null));
        yDividerPos.current = e.clientY;
    };


    useEffect(() => {
        setData(assignIdRows(props.data));
    }, [props.data]);

    useEffect(() => {
        setSelectedItem(props.selectedItem);
    }, [props.selectedItem]);

    useEffect(() => {
        pluginSettingsLoad();
    }, []);

    useEffect(() => {
        if (useUiService && gridColumnsToSave.save) {
            appCtx.uiApiService.uiPluginSettingsRequest.save(
                {
                    idUser: userCtx.idGlobal,
                    name: pluginSettings.name,
                    mnemocode: pluginSettings.mnemocode,
                    gridColumns: gridColumnsToSave.columns,
                    detailsPlugins: [] as string[],
                    rowHeight: pluginSettings.rowHeight ?? GridRowHeightEnum.normal,
                },
                () => {
                    setPluginSettings({ ...pluginSettings, columns: gridColumnsToSave.columns });
                    setGridColumnsToSave({ ...gridColumnsToSave, save: false });
                }
            );
        }
    }, [gridColumnsToSave]);

    useEffect(() => {
        if (props.separator) {
            if (!clientHeight) {
                setClientHeight(getClientHeight(data.length));
                return;
            }
            if (tableRef.current) {
                tableRef.current.style.minHeight = clientHeight + "px";
                tableRef.current.style.maxHeight = clientHeight + "px";
            }
        }
    }, [clientHeight, tableRef.current]);

    useEffect(() => {
        if (props.separator) {
            setClientHeight(getClientHeight(data.length));
        }
    }, [data.length]);

    useLayoutEffect(() => {
        if (savedScrollLeft.current !== null && tableRef.current) {
            tableRef.current.scrollLeft = savedScrollLeft.current;
            if (!gridColumnsToSave.save) {
                scrollLockCleanup.current?.();
                scrollLockCleanup.current = null;
                savedScrollLeft.current = null;
            }
        }
    }, [pluginSettings.columns, gridColumnsToSave.save]);

    const pluginSettingsLoad = () => {
        if (useUiService) {
            setLoadingRow(LoadingStatus.InProcess);
            appCtx.uiApiService.uiPluginSettingsRequest.load(pluginSettings.mnemocode, userCtx.idGlobal, (e) => {
                if (e.respType === "isCompleted") {
                    const columns: IGridColumn[] = [];
                    e.data.gridColumns.forEach((item) => columns.push(item));
                    const plugin: IPluginSettings = {
                        name: e.data.name,
                        mnemocode: e.data.mnemocode,
                        columns: [...columns],
                        permission: pluginSettings.permission,
                        rowHeight: e.data.rowHeight,
                    };
                    setPluginSettings({ ...plugin });
                    setLoadingRow(LoadingStatus.Completed);
                } else {
                    appCtx.uiApiService.uiPluginSettingsRequest.save(
                        {
                            idUser: userCtx.idGlobal,
                            name: pluginSettings.name,
                            mnemocode: pluginSettings.mnemocode,
                            gridColumns: pluginSettings.columns,
                            detailsPlugins: [] as string[],
                            rowHeight: pluginSettings.rowHeight ?? GridRowHeightEnum.normal,
                        },
                        () => setLoadingRow(LoadingStatus.Completed)
                    );
                }
            });
        } else {
            setLoadingRow(LoadingStatus.Completed);
        }
    };

    function getContextId(baseId: string) {
        return `ctxMenu_header_${baseId}`;
    }

    useEffect(() => {
        const handler = () => setGridHeaderContextMenuVisible(false);
        if (gridHeaderContextMenuVisible === true) {
            let ctxMenu = document.getElementById(getContextId(props.gridId)) as HTMLElement;
            if (ctxMenu) {
                ctxMenu.onmouseleave = handler;
                return ctxMenu.removeEventListener("mouseleave", handler);
            }
        }
    }, [gridHeaderContextMenuVisible]);

    const saveColumnWidths = () => {
        const grid = document.getElementById(props.gridId) as HTMLElement;
        if (!grid) return;
        const columnCells = grid.getElementsByClassName(defaultStyles.gridColumnCell);
        const newState: IGridColumn[] = [...pluginSettings.columns];
        let changed = false;
        for (let i = 0; i < columnCells.length; i++) {
            const pName = columnCells[i].getAttribute("data-property-name");
            const column = newState.find((x) => x.propertyName === pName);
            const rect = columnCells[i].getBoundingClientRect().width;
            if (column && column.width !== rect) {
                changed = true;
                column.width = parseInt(String(rect));
            }
        }
        if (changed) {
            setGridColumnsToSave({ columns: newState, save: true });
        }
    };

    const buildTreeMaps = (source: any[]) => {
        const childrenMap = new Map<string, any[]>();
        const roots: any[] = [];

        for (const item of source) {
            const parentRef = item[props.idChildren];
            if (!parentRef) {
                roots.push(item);
            } else {
                if (!childrenMap.has(parentRef)) {
                    childrenMap.set(parentRef, []);
                }
                childrenMap.get(parentRef)!.push(item);
            }
        }

        return { roots, childrenMap };
    };

    const convertToRow = (rawData: any): IGridRow => {
        const cells: IGridCell[] = [];

        Object.entries(rawData).forEach(([propertyName, value]) => {
            try {
                const column = pluginSettings.columns.find((c) => c.propertyName === propertyName);
                let val: any = value;

                const translation =
                    column?.translation ??
                    props.plugin.columns.find((x) => x.propertyName === propertyName)?.translation;
                const conversion =
                    column?.conversion ??
                    props.plugin.columns.find((x) => x.propertyName === propertyName)?.conversion;

                if (translation) {
                    val = Object.entries(rawData).find((x) => x[0] === translation)?.[1];
                }
                if (conversion) {
                    val = reverseConversionWithType(conversion, val, true);
                }

                const nullable =
                    column?.nullable ??
                    props.plugin.columns.find((x) => x.propertyName === propertyName)?.nullable;

                if (val && val !== null && val !== "" && column) {
                    switch (column.propertyType) {
                        case "boolean":
                            val = (val as boolean).toYesNoString();
                            break;
                        case "date":
                            val = typeof val !== "string" ? (val as DateTime).toF3DateFormat(userCtx) : null;
                            break;
                        case "datetime":
                            val = typeof val !== "string" ? (val as DateTime).toF3DateTimeFormat(userCtx) : null;
                            break;
                    }
                }

                cells.push({
                    value: val,
                    propertyName,
                    order: column?.order as number,
                    propertyType: column?.propertyType as string,
                    visibility: pluginSettings.columns.find((x) => x.propertyName === propertyName)
                        ?.visibility as boolean,
                    nullable,
                });
            } catch {
                // skip unknown fields
            }
        });

        return {
            idGlobal: rawData["idGlobal"] as string,
            idRow: rawData["idRow"] as string,
            displayName: rawData["displayName"] || rawData["name"],
            cells: cells
                .filter((c) => c.order !== undefined && c.order !== null)
                .sort(compareByOrder),
            isDeleted: rawData["deleted"] as boolean,
            isLocked: rawData["isLocked"] as boolean,
            isSelected: rawData["idRow"] === selectedItem?.idRow,
        };
    };

    const getVisibleNodes = (
        items: any[],
        childrenMap: Map<string, any[]>,
        depth = 0
    ): ITreeVisibleNode[] => {
        const result: ITreeVisibleNode[] = [];
        for (const rawData of items) {
            const ownId = rawData[props.idParent];
            const children = childrenMap.get(ownId) ?? [];
            const hasChildren = rawData.hasChildren !== undefined
                ? rawData.hasChildren
                : children.length > 0;
            const row = convertToRow(rawData);
            const nodeId = props.nodeIdField
                ? rawData[props.nodeIdField]
                : rawData.idRow;
            result.push({ rawData, row, depth, hasChildren });
            const isExpanded = props.expandedIds
                ? props.expandedIds.has(nodeId)
                : !internalCollapsedIds.has(nodeId);
            if (hasChildren && isExpanded && children.length > 0) {
                result.push(...getVisibleNodes(children, childrenMap, depth + 1));
            }
        }
        return result;
    };

    const toggleExpand = (id: string) => {
        if (props.onToggleExpand) {
            props.onToggleExpand(id);
        } else {
            setInternalCollapsedIds((prev) => {
                const next = new Set(prev);
                next.has(id) ? next.delete(id) : next.add(id);
                return next;
            });
        }
    };

    useEffect(() => {
        if (!props.autoExpandOnLoad) return;
        const treeData = props.changeData ? props.changeData(data) : data;

        if (props.expandedIds === undefined) return;

        if (!props.onToggleExpand) return;

        const parentIds = new Set(
            treeData.map((item) => item[props.idChildren]).filter(Boolean)
        );
        treeData.forEach((item) => {
            const id = props.nodeIdField ? item[props.nodeIdField] : item.idRow;
            if (!id || automaticallyExpandedIds.current.has(id)) return;

            const ownId = item[props.idParent];
            const hasChildren = item.hasChildren !== undefined
                ? item.hasChildren
                : parentIds.has(ownId);

            automaticallyExpandedIds.current.add(id);
            if (hasChildren && !props.expandedIds?.has(id)) {
                props.onToggleExpand?.(id);
            }
        });
    }, [data, props.changeData, props.expandedIds, props.idChildren, props.idParent, props.nodeIdField, props.onToggleExpand]);

    const visibleNodes = useMemo(() => {
        const treeData = props.changeData ? props.changeData(data) : data;
        if (props.flat) {
        return treeData.map((rawData) => ({
            rawData,
            row: convertToRow(rawData),
            depth: 0,
            hasChildren: false,
        }));
    }

        const { roots, childrenMap } = buildTreeMaps(treeData);
        return getVisibleNodes(roots, childrenMap);
    }, [data, props.changeData, getVisibleNodes]);

    const maxDepth = visibleNodes.reduce((max, n) => Math.max(max, n.depth), 0);
    const treeColWidth = Math.max(50, maxDepth * 25 + 38);

    const displayColumns = useMemo(() => {
        if (gridColumnsToSave.save && gridColumnsToSave.columns.length > 0) {
            return gridColumnsToSave.columns;
        }
        return pluginSettings.columns;
    }, [gridColumnsToSave, pluginSettings.columns]);

    const totalTableWidth = useMemo(() => {
        return displayColumns
            .filter(c => c.visibility)
            .reduce((sum, c) => sum + (c.width || 100), 0) + treeColWidth;
    }, [displayColumns, treeColWidth]);

    const handleGridScroll = (e: UIEvent<HTMLDivElement>) => {
        setScrollLeft(e.currentTarget.scrollLeft);
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!['ArrowUp', 'ArrowDown', 'Enter', 'Tab'].includes(e.key)) {
                return;
            }

            if (activeIndex === -1 && visibleNodes.length > 0) {
                setActiveIndex(0);
                e.preventDefault();
                return;
            }

            let newIndex = activeIndex;

            if (e.key === 'ArrowDown' || e.key === 'Tab') {
                newIndex = Math.min(activeIndex + 1, visibleNodes.length - 1);
            } else if (e.key === 'ArrowUp') {
                newIndex = Math.max(activeIndex - 1, 0);
            } else if (e.key === 'Enter') {
                const item = visibleNodes[activeIndex]?.row;
                if (item && props.onDoubleClick) {
                    e.preventDefault();
                    props.onDoubleClick(item);
                }
                return;
            }

            if (newIndex !== activeIndex && newIndex >= 0 && newIndex < visibleNodes.length) {
                e.preventDefault();
                e.stopPropagation();

                setActiveIndex(newIndex);
                const newItem = visibleNodes[newIndex].row;
                setSelectedItem(newItem);
                props.onSelect?.(newItem);

                const viewport = document.querySelector('.VirtualGrid__viewport');
                if (viewport) {
                    const itemHeight = pluginSettings.rowHeight || FIXED_ROW_HEIGHT;
                    const elementTop = newIndex * itemHeight;
                    const viewportTop = viewport.scrollTop;
                    const viewportBottom = viewportTop + viewport.clientHeight;

                    if (elementTop < viewportTop) {
                        viewport.scrollTo({
                            top: elementTop,
                            behavior: 'smooth'
                        });
                    } else if (elementTop + itemHeight > viewportBottom) {
                        viewport.scrollTo({
                            top: elementTop - viewport.clientHeight + itemHeight,
                            behavior: 'smooth'
                        });
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown, { passive: false });
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [visibleNodes, activeIndex, pluginSettings.rowHeight, props.onSelect, props.onDoubleClick]);

    useLayoutEffect(() => {
        const node = visibleNodes[activeIndex];
        if (!node) return;

        const row = document.querySelector(
            `tr[data-item-id="${node.row.idGlobal}"]`
        ) as HTMLElement | null;

        row?.focus();
    }, [activeIndex, visibleNodes]);

    useEffect(() => {
        setSelectedItem(props.selectedItem);
    }, [props.selectedItem]);

    const TreeRowComponent = ({
        index,
        style,
        visibleNodes,
        pluginSettings,
        columns,
        expandedIds,
        collapsedIds,
        loadingNodes,
        selectedItem,
        onToggleExpand,
        onSelect,
        onDoubleClick,
        getStyleRow,
        treeColWidth,
        nodeIdField,
        idParent,
        idChildren,
        props
    }: { index: number; style: React.CSSProperties } & ITreeRowComponentData) => {

        const node = visibleNodes[index];
        if (!node) return null;

        const { rawData, row, depth, hasChildren } = node;
        const isLoadingChildren = loadingNodes?.has(rawData.idGlobal) || false;
        const nodeId = nodeIdField ? rawData[nodeIdField] : rawData.idRow;
        const isSelected =  nodeIdField ? selectedItem?.idGlobal === row.idGlobal : row.isSelected;

        const isExpanded = expandedIds
            ? expandedIds.has(nodeId)
            : !collapsedIds.has(nodeId);

        const cellsByPropertyName = new Map(
            row.cells.map((cell) => [cell.propertyName, cell])
        );
        const orderedCells: IGridCellWithWidth[] = columns
            .filter((column) => column.visibility)
            .sort(compareByOrder)
            .map((column) => ({
                ...cellsByPropertyName.get(column.propertyName),
                propertyName: column.propertyName,
                propertyType: column.propertyType,
                nullable: column.nullable,
                value: cellsByPropertyName.get(column.propertyName)?.value,
                visibility: true,
                order: column.order,
                width: column.width || 100,
            }));

        const antIcon = <LoadingOutlined style={{ fontSize: 16 }} spin />;

      
        return (
            <tr
                key={`tree-row-${index}`}
                tabIndex={-1}
                data-item-id={row.idGlobal}
                data-selected={row.isSelected ? "true" : "false"}
                className={classNames(
                    defaultStyles.gridRow,
                    getStyleRow?.(row),
                    isSelected  ? defaultStyles.gridRowSelected : null,
                    row.isDeleted ? defaultStyles.gridRowDeleted : null,
                    row.isLocked ? defaultStyles.gridRowLocked : null
                )}
                style={{
                    ...style,
                    height: pluginSettings.rowHeight || FIXED_ROW_HEIGHT,
                    display: 'flex',
                    width: 'max-content',
                    minWidth: 'max-content'
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex(index);

                    if (node.row.isSelected) {
                        setSelectedItem(undefined)
                        props.onSelect?.(undefined)
                    } else {
                        setSelectedItem(node.row)
                        props.onSelect?.(node.row)
                    }
                }}
                onDoubleClick={() => onDoubleClick?.(row)}
                onKeyDown={(e) => {
                    if (e.key === "Escape" && selectedItem) {
                        onSelect?.(undefined);
                    }
                }}
            >
                {/* Колонка дерева: отступ + иконка expand/collapse */}
                <td
                    className={classNames(styles.treeColumn, styles.treeColumnData)}
                    style={{
                        width: treeColWidth,
                        minWidth: treeColWidth,
                        height: pluginSettings.rowHeight || FIXED_ROW_HEIGHT,
                        flexShrink: 0
                    }}
                >
                    <div
                        className={styles.treeIndent}
                        style={{ paddingLeft: depth * 16 }}
                    >
                        <button
                            className={styles.treeExpandButton}
                            onClick={() => {
                                if (hasChildren) onToggleExpand(nodeId);
                            }}
                            disabled={isLoadingChildren}
                        >
                            <div className={styles.treeChevron}>
                                {isLoadingChildren ? (
                                    <Spin indicator={antIcon} style={{ marginLeft: 8, color: '#34495e' }} />
                                ) : (
                                    hasChildren && (
                                        isExpanded
                                            ? <DownIcon className={styles.treeSvg} />
                                            : <RightIcon className={styles.treeSvg} />
                                    )
                                )}
                            </div>
                            {!isLoadingChildren && (
                                hasChildren
                                    ? isExpanded
                                        ? <FolderMinusIcon className={styles.treeSvg} />
                                        : <FolderPlusIcon className={styles.treeSvg} />
                                    : <FolderIcon className={styles.treeSvg} />
                            )}
                        </button>
                    </div>
                </td>

                {/* Колонки данных */}
                {orderedCells.map(
                    (cell, cellIndex) =>
                        cell.visibility && (
                            <TreeGridTd
                                key={cellIndex}
                                item={cell}
                                rowHeight={pluginSettings.rowHeight || FIXED_ROW_HEIGHT}
                            />
                        )
                )}
            </tr>
        );
    };

    const renderVirtualizedGrid = () => {
        const containerHeight = clientHeight || 440;
        const listHeight = Math.min(
            visibleNodes.length * rowHeight + SCROLLBAR_HEIGHT,
            containerHeight - rowHeight
        );

        const rowData: ITreeRowComponentData = {
            visibleNodes,
            pluginSettings,
            columns: displayColumns,
            expandedIds: props.expandedIds,
            collapsedIds: internalCollapsedIds,
            loadingNodes: props.loadingNodes,
            selectedItem,
            onToggleExpand: toggleExpand,
            onSelect: (row) => {
                setSelectedItem(row);
                props.onSelect?.(row);
            },
            onDoubleClick: props.onDoubleClick,
            getStyleRow: props.getStyleRow,
            treeColWidth,
            nodeIdField: props.nodeIdField,
            idParent: props.idParent,
            idChildren: props.idChildren,
            props
        };

        return (
            <div
                className={styles.gridScrollContainer}
                ref={tableRef}
                style={{
                    height: containerHeight,
                    overflow: 'hidden'
                }}
            >
                <div
                    className={styles.gridHeaderWrapper}
                    style={{
                        transform: `translateX(-${scrollLeft}px)`,
                        minWidth: totalTableWidth
                    }}
                >
                    <table
                        id={props.gridId}
                        className={defaultStyles.gridTable}
                        style={{
                            width: totalTableWidth,
                            minWidth: totalTableWidth,
                            marginBottom: 0
                        }}
                    >
                        <thead
                            className={defaultStyles.gridColumnsHeader}
                            style={{ height: pluginSettings.rowHeight }}
                            onContextMenu={(e) => {
                                if (process.env.REACT_APP_SHOW_GRID_CONTEXT === "true") {
                                    e.preventDefault();

                                    const td = (e.target as HTMLElement).closest('td[data-property-name]') as HTMLElement | null;
                                    const propertyName = td?.dataset.propertyName;

                                    const column = propertyName
                                        ? pluginSettings.columns.find(c => c.propertyName === propertyName)
                                        : undefined;

                                    setContextMenuColumn(column);

                                    setHeaderCtxPosX(e.clientX);
                                    setHeaderCtxPosY(e.clientY);
                                    setGridHeaderContextMenuVisible(true);
                                }
                            }}
                        >
                            <tr
                                className={defaultStyles.gridColumn}
                                onMouseUp={saveColumnWidths}
                                style={{
                                    display: 'flex',
                                    width: '100%',
                                }}
                            >
                                <td
                                    draggable={false}
                                    className={styles.treeColumn}
                                    style={{
                                        width: treeColWidth,
                                        minWidth: treeColWidth,
                                        flexShrink: 0
                                    }}
                                />

                                {displayColumns
                                    .filter((c) => c.visibility)
                                    .sort(compareByOrder)
                                    .map((item, index) => (
                                        <TableHeader
                                            key={`th-${index}`}
                                            index={index}
                                            draggable={true}
                                            item={item}
                                            filtered={false}
                                            pluginSettings={pluginSettings}
                                            setGridColumnsToSave={setGridColumnsToSave}
                                            sorting={false}
                                            onSort={() => { }}
                                            searching={false}
                                            onSearch={() => { }}
                                            renderSortIcon={() => <></>}
                                            onFilterDelete={() => { }}
                                            setLoadingStatus={props.setLoadingStatus}
                                            onPageNumberChange={() => { }}
                                            onResizeStart={() => {
                                                const el = tableRef.current;
                                                if (!el) return;
                                                const lockedScrollLeft = el.scrollLeft;
                                                savedScrollLeft.current = lockedScrollLeft;
                                                let rafId: number | null = null;
                                                const lock = () => {
                                                    if (el.scrollLeft !== lockedScrollLeft) {
                                                        el.scrollLeft = lockedScrollLeft;
                                                    }
                                                    rafId = requestAnimationFrame(lock);
                                                };
                                                rafId = requestAnimationFrame(lock);
                                                scrollLockCleanup.current = () => {
                                                    if (rafId !== null) cancelAnimationFrame(rafId);
                                                };
                                            }}
                                            onResize={(column, width) => {
                                                const container = gridContainerRef.current;
                                                if (!container) return;

                                                const liveTableWidth = displayColumns
                                                    .filter((item) => item.visibility)
                                                    .reduce(
                                                        (sum, item) => sum + (
                                                            item.propertyName === column.propertyName
                                                                ? width
                                                                : item.width || 100
                                                        ),
                                                        treeColWidth
                                                    );

                                                container
                                                    .querySelectorAll<HTMLElement>("[data-property-name]")
                                                    .forEach((cell) => {
                                                        if (cell.dataset.propertyName !== column.propertyName) return;

                                                        cell.style.width = `${width}px`;
                                                        cell.style.minWidth = `${width}px`;
                                                    });

                                                const headerTable = container.querySelector<HTMLElement>("table");
                                                if (headerTable) {
                                                    headerTable.style.width = `${liveTableWidth}px`;
                                                    headerTable.style.minWidth = `${liveTableWidth}px`;

                                                    const headerWrapper = headerTable.parentElement;
                                                    if (headerWrapper) {
                                                        headerWrapper.style.minWidth = `${liveTableWidth}px`;
                                                    }
                                                }

                                            }}
                                            onReorderEnd={() => {
                                                const el = tableRef.current;
                                                if (!el) return;
                                                const lockedScrollLeft = el.scrollLeft;
                                                savedScrollLeft.current = lockedScrollLeft;
                                                let rafId: number | null = null;
                                                const lock = () => {
                                                    if (el.scrollLeft !== lockedScrollLeft) {
                                                        el.scrollLeft = lockedScrollLeft;
                                                    }
                                                    rafId = requestAnimationFrame(lock);
                                                };
                                                rafId = requestAnimationFrame(lock);
                                                scrollLockCleanup.current = () => {
                                                    if (rafId !== null) cancelAnimationFrame(rafId);
                                                };
                                            }}
                                            cls={{
                                                tD: defaultStyles.gridColumnCell,
                                                tooltip: defaultStyles.gridColumnHeaderName,
                                                sortingDiv: defaultStyles.gridColumnCellButtons,
                                                sortingDivButton: defaultStyles.gridSortButton,
                                                searchDropDivButton: defaultStyles.searchDropDivButton,
                                                filteredDiv: defaultStyles.gridFilterButton,
                                            }}
                                        />
                                    ))}
                            </tr>
                        </thead>
                    </table>
                </div>

                <VirtualGrid
                    style={{
                        height: listHeight,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                    }}
                    rowComponent={TreeRowComponent}
                    rowCount={visibleNodes.length}
                    rowHeight={rowHeight}
                    scrollWidth={SCROLLBAR_HEIGHT}
                    rowProps={rowData}
                    overscanCount={5}
                    activeIndex={activeIndex}
                    onScroll={handleGridScroll}
                    loading={props.loadingStatus === LoadingStatus.InProcess}
                    getRowKey={(index) => visibleNodes[index].row.idGlobal}
                />
            </div>
        );
    };

    // ── Render ──

    if (loadingRow === LoadingStatus.InProcess) return <Spinner />;

    if (props.loadingStatus === LoadingStatus.InProcess) return <DocumentOperationSpinner xl />;

    if (props.loadingStatus === LoadingStatus.NoAccess)
        return <div className={defaultStyles.noDataInfo}><span>{baseT("noAccess")}</span></div>;

    if (props.loadingStatus === LoadingStatus.NoData)
        return <div className={defaultStyles.noDataInfo}><span>{baseT("noData")}</span></div>;

    return (
        <div className={defaultStyles.gridWrapper} ref={gridContainerRef} style={gridFontStyle}>
            <div className={defaultStyles.rowAboveTable}>
                {props.showQuantity && (
                    <div className={defaultStyles.quantityPositions}>
                       {baseT("totalCount")}: <b>{props.totalCount}</b>
                    </div>
                )}
                {!props.hideSettingstButton && (
                    <div className={styles.buttonsContainer}>
                        <div className={styles.buttonLink} onClick={() => setShowGridHeaderSettings(true)}>{baseT("gridSettings")}</div>
                    </div>
                )}
            </div>

            {renderVirtualizedGrid()}

            {props?.separator && <div onMouseDown={onMouseHoldDown} className={classNames(businessStyles.separator, businessStyles.separator_horizontal)}></div>}

            {gridHeaderContextMenuVisible && (
                <ContextMenu
                    id={getContextId(props.gridId)}
                    xPos={headerCtxPosX}
                    yPos={headerCtxPosY}
                    items={[
                        {
                            name: baseT("gridSettings"),
                            onClick: () => setShowGridHeaderSettings(true),
                        },
                        ...(contextMenuColumn ? [{
                            name: baseT("resetColumnWidth"),
                            onClick: () => {
                                const defaultColumn = defaultPlugin.columns.find(
                                    c => c.propertyName === contextMenuColumn.propertyName
                                );
                                const width = defaultColumn?.width ?? 100;

                                const container = gridContainerRef.current;
                                if (container) {
                                    container
                                        .querySelectorAll<HTMLElement>(
                                            `[data-property-name="${contextMenuColumn.propertyName}"]`
                                        )
                                        .forEach(cell => {
                                            cell.style.width = `${width}px`;
                                            cell.style.minWidth = `${width}px`;
                                        });
                                }

                                const updatedColumns = pluginSettings.columns.map(c =>
                                    c.propertyName === contextMenuColumn.propertyName
                                        ? { ...c, width }
                                        : c
                                );

                                setPluginSettings(prev => ({ ...prev, columns: updatedColumns }));
                                setGridColumnsToSave({ columns: updatedColumns, save: true });
                            },
                        }] : []),
                    ]}
                />
            )}
            {showGridHeaderSettings && (
                <GridUISettings
                    plugin={pluginSettings}
                    defaultPlugin={defaultPlugin}
                    save={(e) => {
                        if (e.columns) {
                            setGridColumnsToSave({ columns: e.columns, save: true });
                        }
                        setPluginSettings({ ...pluginSettings, rowHeight: e.rowHeight });
                        setGridHeaderContextMenuVisible(false);
                        setShowGridHeaderSettings(false);
                    }}
                    cancel={() => {
                        setGridHeaderContextMenuVisible(false);
                        setShowGridHeaderSettings(false);
                    }}
                />
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  TreeGridTd — ячейка данных
// ─────────────────────────────────────────────────────────────────────────────

interface ITreeGridTdProps {
    item: IGridCellWithWidth;
    rowHeight?: number;
}

const TreeGridTd: FC<ITreeGridTdProps> = ({ item, rowHeight }) => {
    const [spanOverflows, setSpanOverflows] = useState<boolean>(false);
    const [formatTooltip, setFormatTooltip] = useState<string>("");
    const [copyVisible, setCopyVisible] = useState<boolean>(false);
    const [copyOpacity, setCopyOpacity] = useState<number>(0.4);
    const tdRef = useRef<HTMLTableCellElement>(null);

    const checkOverflow = () => {
        const el = tdRef.current;
        if (!el) return;
        const css = getComputedStyle(el);
        const innerWidth =
            el.offsetWidth - parseFloat(css.paddingLeft) - parseFloat(css.paddingRight) - 5;
        const span = el.querySelector("span");
        setSpanOverflows((span?.scrollWidth ?? 0) > innerWidth);
    };

    return (
        <td
            ref={tdRef}
            className={defaultStyles.gridRowCell}
            style={{ width: item.width, minWidth: item.width, height: rowHeight, cursor: "default" }}
            data-order-number={item.order}
            data-property-name={item.propertyName}
            onMouseEnter={() => {
                checkOverflow();
                setCopyVisible(true);
            }}
            onMouseLeave={() => setCopyVisible(false)}
        >
            <Tooltip
                className={defaultStyles.gridRowCellText}
                title={spanOverflows ? formatTooltip : undefined}
                placement="top"
                autoAdjustOverflow={false}
                overlayStyle={{ maxWidth: 300 }}
                getPopupContainer={() => document.body}
            >
                <span style={{ width: "100%" }}>
                    <FormattedValueView newValue={item} setFormatTooltip={setFormatTooltip} />
                </span>
            </Tooltip>
            <span
                style={{
                    opacity: copyVisible ? copyOpacity : 0,
                    transition: "opacity 0.3s ease-in",
                    marginRight: -10,
                    paddingLeft: 1,
                    width: 16,
                    height: 16,
                    cursor: "pointer",
                    backgroundColor: "white",
                    borderRadius: 3,
                }}
                onClick={async () => {
                    await copyToClipboard(getFormattedCellValue(item));
                }}
            >
                <ActionIcons.CopyMiniIcon
                    onMouseEnter={() => setCopyOpacity(0.85)}
                    onMouseLeave={() => setCopyOpacity(0.4)}
                />
            </span>
        </td>
    );
};